import React, { useState, useEffect } from 'react';
import axios from 'axios';

const FONT = "'Inter', system-ui, sans-serif";

const Icon = ({ d, size = 18, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const COLUMNS = [
    { label: 'Booking ID', width: '120px' },
    { label: 'Passenger Name', width: '160px' },
    { label: 'Client Name', width: '160px' },
    { label: 'Trip Name', width: '200px' },
    { label: 'Pickup Stop', width: '160px' },
    { label: 'Drop Stop', width: '160px' },
    { label: 'Booked Date & Time', width: '180px' },
    { label: 'Status', width: '110px' },
    { label: 'Actions', width: '120px' },
];

export default function ShuttleBooking({ user }) {
    const [search, setSearch] = useState('');
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [routeFilter, setRouteFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [expandedTrips, setExpandedTrips] = useState({});

    useEffect(() => {
        const fetchBookings = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`/api/shuttle-bookings/${user?.operatorId || 'all'}`);
                setBookings(res.data);
            } catch (err) {
                console.error("Error fetching shuttle bookings:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchBookings();
    }, [user]);

    const uniqueRoutes = [...new Set(bookings.map(b => b.tripId?.tripName).filter(Boolean))];
    const getUILabel = (status) => (status === 'Pending' || status === 'Allocated') ? 'Allocated' : (status || 'Unknown');
    const uniqueStatuses = [...new Set(bookings.map(b => getUILabel(b.status)))];

    const filtered = bookings.filter(b => {
        const matchesSearch = !search || 
            (b.passengerId?.firstName || '').toLowerCase().includes(search.toLowerCase()) || 
            (b.passengerId?.lastName || '').toLowerCase().includes(search.toLowerCase()) ||
            (b.tripId?.tripName || '').toLowerCase().includes(search.toLowerCase());
            
        const matchesRoute = !routeFilter || b.tripId?.tripName === routeFilter;
        const matchesStatus = !statusFilter || getUILabel(b.status) === statusFilter;
        const matchesDate = !dateFilter || new Date(b.createdAt).toISOString().split('T')[0] === dateFilter;

        return matchesSearch && matchesRoute && matchesStatus && matchesDate;
    });

    const groupedBookings = React.useMemo(() => {
        const groups = {};
        filtered.forEach(b => {
            const tId = b.tripId?._id || 'unassigned';
            if (!groups[tId]) {
                groups[tId] = {
                    trip: b.tripId,
                    client: b.passengerId?.clientId?.companyName || b.tripId?.clientId?.companyName || 'N/A',
                    bookings: []
                };
            }
            groups[tId].bookings.push(b);
        });
        return Object.values(groups);
    }, [filtered]);

    const toggleTrip = (tId) => {
        setExpandedTrips(prev => ({ ...prev, [tId]: !prev[tId] }));
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: FONT }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
                @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
                @keyframes shimmer-pulse { 0%,100%{opacity:0.5} 50%{opacity:1} }
                .sb-header-btn:hover { background: rgba(255,255,255,0.25) !important; }
                .sb-search:focus { outline: none; box-shadow: 0 0 0 3px rgba(99,102,241,0.15); border-color: #00B2A9 !important; }
            `}</style>

            {/* ── Banner ── */}
            <div style={{
                background: 'linear-gradient(135deg, #16539C 0%, #00B2A9 50%, #818cf8 100%)',
                padding: '32px 44px 36px', position: 'relative', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                <div style={{ position: 'absolute', bottom: -60, right: 120, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                <div style={{ position: 'relative', zIndex: 2 }}>

                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div style={{ width: 52, height: 52, borderRadius: 16, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }}>
                                    <Icon d={['M8 6v12', 'M16 6v12', 'M3 12h18', 'M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z']} size={24} color="white" />
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Trip Operations</div>
                                    <h1 style={{ fontSize: 28, fontWeight: 900, color: 'white', margin: 0, letterSpacing: '-0.02em' }}>Shuttle with Booking</h1>
                                </div>
                            </div>
                            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13.5, marginTop: 10, marginBottom: 0, maxWidth: 520 }}>
                                Manage scheduled shuttle services with seat-based bookings, route stops, and passenger assignments.
                            </p>
                        </div>

                        {/* Stats pills */}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {[
                                { label: 'Total Bookings', value: bookings.length, color: '#a5b4fc' },
                                { label: 'Active Shuttles', value: new Set(bookings.map(b => b.tripId?._id)).size, color: '#6ee7b7' },
                                { label: 'Today\'s Trips', value: 0, color: '#fde68a' },
                            ].map(s => (
                                <div key={s.label} style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 14, padding: '12px 20px', textAlign: 'center', minWidth: 90 }}>
                                    <div style={{ fontSize: 28, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Toolbar ── */}
            <div style={{ padding: '24px 44px 0', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                {/* Search */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'white', borderRadius: 12, border: '1.5px solid #e2e8f0', padding: '0 14px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)', flex: 1, maxWidth: 380 }}>
                    <Icon d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={15} color="#94a3b8" />
                        <input
                        className="sb-search"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search bookings..."
                        style={{ flex: 1, border: 'none', outline: 'none', padding: '11px 0', fontSize: 13.5, fontFamily: FONT, color: '#334155', background: 'transparent' }}
                    />
                </div>

                {/* Filters */}
                <select
                    value={routeFilter}
                    onChange={(e) => setRouteFilter(e.target.value)}
                    style={{ background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 600, color: '#475569', outline: 'none' }}
                >
                    <option value="">All Routes</option>
                    {uniqueRoutes.map(r => <option key={r} value={r}>{r}</option>)}
                </select>

                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 600, color: '#475569', outline: 'none' }}
                >
                    <option value="">All Statuses</option>
                    {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>

                <input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    style={{ background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, color: '#475569', outline: 'none', fontFamily: FONT }}
                />

            </div>

            {/* ── Datatable ── */}
            <div style={{ padding: '20px 44px 44px', animation: 'fadeUp 0.35s ease' }}>
                <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>

                    {/* Table header */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #f1f5f9' }}>
                                    <th style={{ padding: '14px 18px', width: 40 }}>
                                        <input type="checkbox" disabled style={{ accentColor: '#00B2A9', cursor: 'not-allowed', opacity: 0.4 }} />
                                    </th>
                                    {COLUMNS.map(col => (
                                        <th key={col.label} style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: col.label === 'Actions' ? 'right' : 'left', whiteSpace: 'nowrap', width: col.width }}>
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* Loading state */}
                                {loading ? (
                                    <tr>
                                        <td colSpan={COLUMNS.length + 1} style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
                                            Loading bookings...
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={COLUMNS.length + 1}>
                                            <div style={{ padding: '64px 40px', textAlign: 'center' }}>
                                                <div style={{ width: 80, height: 80, borderRadius: 24, background: 'linear-gradient(135deg,#eef2ff,#e6f0fa)', border: '2px solid #c3dcf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                                    <Icon d={['M8 6v12', 'M16 6v12', 'M3 12h18', 'M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z']} size={36} color="#00B2A9" />
                                                </div>
                                                <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
                                                    No Bookings Found
                                                </div>
                                                <div style={{ fontSize: 14, color: '#94a3b8', maxWidth: 400, margin: '0 auto', lineHeight: 1.6 }}>
                                                    Bookings made by passengers will appear here.
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    groupedBookings.map(group => (
                                        <React.Fragment key={group.trip?._id || 'unassigned'}>
                                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => toggleTrip(group.trip?._id || 'unassigned')}>
                                                <td colSpan={COLUMNS.length + 1} style={{ padding: '16px 18px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                        <div style={{ width: 32, height: 32, borderRadius: 10, background: expandedTrips[group.trip?._id || 'unassigned'] ? '#16539C' : '#eef2ff', color: expandedTrips[group.trip?._id || 'unassigned'] ? 'white' : '#16539C', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                                                            <Icon d={expandedTrips[group.trip?._id || 'unassigned'] ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} size={18} />
                                                        </div>
                                                        <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>
                                                            {group.trip?.tripName || 'Unassigned Bookings'}
                                                        </div>
                                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', background: 'rgba(0,0,0,0.05)', padding: '4px 10px', borderRadius: 12 }}>
                                                            {group.client}
                                                        </div>
                                                        <div style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: '#00B2A9', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '6px 14px', borderRadius: 20 }}>
                                                            {group.bookings.length} Bookings
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedTrips[group.trip?._id || 'unassigned'] && group.bookings.map(booking => (
                                                <tr key={booking._id} style={{ borderBottom: '1px solid #f1f5f9', background: 'white' }}>
                                                    <td style={{ padding: '14px 18px', width: 40 }}></td>
                                                    <td style={{ padding: '14px 18px', fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                                                        #{booking._id.substring(booking._id.length - 6).toUpperCase()}
                                                    </td>
                                                    <td style={{ padding: '14px 18px', fontSize: 14, fontWeight: 500, color: '#334155' }}>
                                                        {booking.passengerId?.firstName} {booking.passengerId?.lastName}
                                                    </td>
                                                    <td style={{ padding: '14px 18px', fontSize: 13, color: '#475569' }}>
                                                        {booking.passengerId?.clientId?.companyName || 'N/A'}
                                                    </td>
                                                    <td style={{ padding: '14px 18px', fontSize: 13, color: '#94a3b8' }}>
                                                        {booking.tripId?.tripName || 'N/A'}
                                                        <div style={{ fontSize: 11, color: booking.tripInstanceId ? '#f59e0b' : '#3b82f6', fontWeight: 600, marginTop: 4, display: 'inline-block', padding: '2px 6px', borderRadius: 4, background: booking.tripInstanceId ? '#fef3c7' : '#eff6ff' }}>
                                                            {booking.tripInstanceId ? `For: ${new Date(booking.tripInstanceId.tripDate).toLocaleDateString()}` : 'Everyday (Panel)'}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '14px 18px', fontSize: 13, color: '#475569' }}>
                                                        {booking.pickupStopId?.name || booking.pickupPoint || 'N/A'}
                                                    </td>
                                                    <td style={{ padding: '14px 18px', fontSize: 13, color: '#475569' }}>
                                                        {booking.dropStopId?.name || booking.dropPoint || 'N/A'}
                                                    </td>
                                                    <td style={{ padding: '14px 18px', fontSize: 13, color: '#64748b' }}>
                                                        <div>{new Date(booking.createdAt).toLocaleDateString()}</div>
                                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{new Date(booking.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                    </td>
                                                    <td style={{ padding: '14px 18px' }}>
                                                        {(() => {
                                                            let bg = '#eef2ff', color = '#16539C', label = booking.status;
                                                            if (booking.status === 'Pending' || booking.status === 'Allocated') {
                                                                label = 'Allocated';
                                                                bg = '#eef2ff'; color = '#16539C';
                                                            } else if (booking.status === 'Boarded') {
                                                                label = 'Boarded';
                                                                bg = '#dcfce7'; color = '#166534';
                                                            } else if (booking.status === 'Not Allocated (Full)') {
                                                                label = 'Not Allocated (Full)';
                                                                bg = '#fee2e2'; color = '#991b1b';
                                                            }
                                                            return (
                                                                <span style={{ padding: '4px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: bg, color: color }}>
                                                                    {label}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                                                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                                                            <Icon d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Table footer */}
                    <div style={{ padding: '14px 20px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>{filtered.length} bookings total</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            {['←', '1', '→'].map(p => (
                                <div key={p} style={{ width: 32, height: 32, borderRadius: 8, border: '1.5px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: p === '1' ? '#00B2A9' : '#94a3b8', background: p === '1' ? '#eef2ff' : 'white', cursor: 'not-allowed' }}>
                                    {p}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 14, fontSize: 12, color: '#94a3b8', textAlign: 'center', fontWeight: 500 }}>
                    🚌 Shuttle with Booking
                </div>
            </div>
        </div>
    );
}
